import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { CurrencyDisplay } from "./CurrencySettings";
import { FileSpreadsheet, Download, Filter } from "lucide-react";
import { formatDateDDMonYYYY } from "../utils/dateFormat";
import DateFilter from "./DateFilter";

const ReportsTable = ({ restaurants }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("restaurant");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get("/brands");
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const getBrandName = (brandId) => {
    if (!brandId) return "Unknown Brand";
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : brandId;
  };

  const getUniqueBrands = () => {
    const brandIds = restaurants
      .map(r => r.brand)
      .filter(brand => brand && brand.trim() !== '');
    const uniqueBrandIds = [...new Set(brandIds)];
    return uniqueBrandIds.map(id => ({
      id,
      name: getBrandName(id)
    }));
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("/revenue-categories");
      setRevenueCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Get unique category names (merge common categories across restaurants)
  const getUniqueCategories = () => {
    const categoryNames = {};
    
    revenueCategories.forEach(cat => {
      const name = cat.name.trim();
      if (!categoryNames[name]) {
        categoryNames[name] = {
          name: name,
          ids: []
        };
      }
      categoryNames[name].ids.push(cat.id);
    });
    
    return Object.values(categoryNames).sort((a, b) => a.name.localeCompare(b.name));
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedRestaurant && selectedRestaurant !== "all") params.append("restaurant_id", selectedRestaurant);

      const response = await axiosInstance.get(`/revenues?${params.toString()}`);
      let revenues = response.data;

      // Filter by brand if selected
      if (selectedBrand && selectedBrand !== "all") {
        revenues = revenues.filter(rev => {
          const restaurant = restaurants.find(r => r.id === rev.restaurant_id);
          return restaurant?.brand === selectedBrand;
        });
      }

      // Filter by categories if selected
      if (selectedCategories.length > 0) {
        // Get all category IDs that match the selected category names
        const selectedCategoryIds = [];
        selectedCategories.forEach(categoryName => {
          const matchingCategories = revenueCategories.filter(cat => cat.name.trim() === categoryName);
          matchingCategories.forEach(cat => selectedCategoryIds.push(cat.id));
        });
        
        revenues = revenues.map(rev => {
          // Only include amounts for selected categories
          const filteredAmounts = {};
          let filteredTotal = 0;
          
          Object.entries(rev.amounts || {}).forEach(([catId, amount]) => {
            if (selectedCategoryIds.includes(catId)) {
              filteredAmounts[catId] = amount;
              filteredTotal += parseFloat(amount);
            }
          });
          
          return {
            ...rev,
            amounts: filteredAmounts,
            total_amount: filteredTotal
          };
        });
        
        // Remove revenues with no matching categories
        revenues = revenues.filter(rev => Object.keys(rev.amounts).length > 0);
      }

      // Process data based on grouping
      let processedData = [];

      if (groupBy === "brand") {
        processedData = groupByBrand(revenues);
      } else if (groupBy === "restaurant") {
        processedData = groupByRestaurant(revenues);
      } else if (groupBy === "category") {
        processedData = groupByCategory(revenues);
      } else if (groupBy === "date") {
        processedData = groupByDate(revenues);
      } else if (groupBy === "all") {
        processedData = groupByAll(revenues);
      }

      setReportData(processedData);
      toast.success("Report generated successfully!");
    } catch (error) {
      toast.error("Failed to generate report");
      console.error("Error generating report:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupByBrand = (revenues) => {
    const grouped = {};
    revenues.forEach((rev) => {
      const restaurant = restaurants.find(r => r.id === rev.restaurant_id);
      const brandName = getBrandName(restaurant?.brand);
      
      if (!grouped[brandName]) {
        grouped[brandName] = {
          brand_name: brandName,
          total_revenue: 0,
          entries: 0,
          categories: {},
        };
      }
      grouped[brandName].total_revenue += rev.total_amount || 0;
      grouped[brandName].entries += 1;
      
      // Aggregate category amounts
      Object.entries(rev.amounts || {}).forEach(([catId, amount]) => {
        if (!grouped[brandName].categories[catId]) {
          grouped[brandName].categories[catId] = 0;
        }
        grouped[brandName].categories[catId] += parseFloat(amount);
      });
    });
    return Object.values(grouped);
  };

  const groupByRestaurant = (revenues) => {
    const grouped = {};
    revenues.forEach((rev) => {
      if (!grouped[rev.restaurant_id]) {
        grouped[rev.restaurant_id] = {
          restaurant_name: rev.restaurant_name,
          total_revenue: 0,
          entries: 0,
          categories: {},
        };
      }
      grouped[rev.restaurant_id].total_revenue += rev.total_amount || 0;
      grouped[rev.restaurant_id].entries += 1;
      
      // Aggregate category amounts
      Object.entries(rev.amounts || {}).forEach(([catId, amount]) => {
        if (!grouped[rev.restaurant_id].categories[catId]) {
          grouped[rev.restaurant_id].categories[catId] = 0;
        }
        grouped[rev.restaurant_id].categories[catId] += parseFloat(amount);
      });
    });
    return Object.values(grouped);
  };

  const groupByCategory = (revenues) => {
    const grouped = {};
    const categoryCounts = {};
    
    revenues.forEach((rev) => {
      if (!rev.amounts) return;
      
      Object.entries(rev.amounts).forEach(([catId, amount]) => {
        const category = revenueCategories.find(c => c.id === catId);
        const catName = category ? category.name : catId;
        
        if (!grouped[catId]) {
          grouped[catId] = {
            category_name: catName,
            total_revenue: 0,
            entries: 0,
          };
          categoryCounts[catId] = new Set();
        }
        grouped[catId].total_revenue += parseFloat(amount || 0);
        categoryCounts[catId].add(rev.id);
      });
    });
    
    // Update entries count based on unique revenue entries
    Object.keys(grouped).forEach(catId => {
      grouped[catId].entries = categoryCounts[catId] ? categoryCounts[catId].size : 0;
    });
    
    return Object.values(grouped);
  };

  const groupByDate = (revenues) => {
    const grouped = {};
    revenues.forEach((rev) => {
      if (!grouped[rev.date]) {
        grouped[rev.date] = {
          date: rev.date,
          total_revenue: 0,
          entries: 0,
          categories: {},
        };
      }
      grouped[rev.date].total_revenue += rev.total_amount || 0;
      grouped[rev.date].entries += 1;
      
      Object.entries(rev.amounts || {}).forEach(([catId, amount]) => {
        if (!grouped[rev.date].categories[catId]) {
          grouped[rev.date].categories[catId] = 0;
        }
        grouped[rev.date].categories[catId] += parseFloat(amount);
      });
    });
    return Object.values(grouped).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const groupByAll = (revenues) => {
    const grouped = {};
    revenues.forEach((rev) => {
      if (!rev || !rev.restaurant_id || !rev.date) return;
      
      const key = `${rev.restaurant_id}_${rev.date}`;
      if (!grouped[key]) {
        grouped[key] = {
          restaurant_name: rev.restaurant_name || "Unknown",
          date: rev.date,
          total_revenue: 0,
          entries: 0,
          categories: {},
        };
      }
      grouped[key].total_revenue += rev.total_amount || 0;
      grouped[key].entries += 1;
      
      if (rev.amounts && typeof rev.amounts === 'object') {
        Object.entries(rev.amounts).forEach(([catId, amount]) => {
          const category = revenueCategories.find(c => c.id === catId);
          const catName = category ? category.name : "Unknown Category";
          if (!grouped[key].categories[catId]) {
            grouped[key].categories[catId] = { name: catName, amount: 0 };
          }
          grouped[key].categories[catId].amount += parseFloat(amount || 0);
        });
      }
    });
    return Object.values(grouped).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const downloadCSV = () => {
    if (reportData.length === 0) {
      toast.error("No data to download");
      return;
    }

    let csvContent = "";
    
    // Generate CSV based on grouping type
    if (groupBy === "brand") {
      csvContent = "Brand,Total Revenue,Entries,Category Breakdown\n";
      reportData.forEach(row => {
        const categoryBreakdown = Object.entries(row.categories || {})
          .map(([catId, amount]) => {
            const category = revenueCategories.find(c => c.id === catId);
            const catName = category ? category.name : "Unknown";
            return `${catName}: ${amount}`;
          })
          .join("; ");
        csvContent += `"${row.brand_name}",${row.total_revenue},${row.entries},"${categoryBreakdown}"\n`;
      });
    } else if (groupBy === "restaurant") {
      csvContent = "Restaurant,Total Revenue,Entries,Category Breakdown\n";
      reportData.forEach(row => {
        const categoryBreakdown = Object.entries(row.categories || {})
          .map(([catId, amount]) => {
            const category = revenueCategories.find(c => c.id === catId);
            const catName = category ? category.name : "Unknown";
            return `${catName}: ${amount}`;
          })
          .join("; ");
        csvContent += `"${row.restaurant_name}",${row.total_revenue},${row.entries},"${categoryBreakdown}"\n`;
      });
    } else if (groupBy === "category") {
      csvContent = "Category,Total Revenue,Entries\n";
      reportData.forEach(row => {
        csvContent += `"${row.category_name}",${row.total_revenue},${row.entries}\n`;
      });
    } else if (groupBy === "date") {
      csvContent = "Date,Total Revenue,Entries\n";
      reportData.forEach(row => {
        csvContent += `"${row.date}",${row.total_revenue},${row.entries}\n`;
      });
    } else if (groupBy === "all") {
      csvContent = "Restaurant,Date,Total Revenue,Entries,Categories\n";
      reportData.forEach(row => {
        const categories = Object.values(row.categories).map(c => `${c.name}: ${c.amount}`).join("; ");
        csvContent += `"${row.restaurant_name}","${row.date}",${row.total_revenue},${row.entries},"${categories}"\n`;
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report_${groupBy}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Report downloaded successfully!");
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Report Filters
          </CardTitle>
          <CardDescription>Configure your report parameters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>Group By</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="brand">Brand</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="all">All (Restaurant + Date + Category)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Filter by Brand</Label>
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger>
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {getUniqueBrands().map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Filter by Restaurant</Label>
              <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
                <SelectTrigger>
                  <SelectValue placeholder="All Restaurants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {restaurants.map((restaurant) => (
                    <SelectItem key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex items-end gap-2">
              <Button
                onClick={generateReport}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
              >
                {loading ? "Generating..." : "Generate Report"}
              </Button>
            </div>
          </div>
          
          {/* Date Filter Row */}
          <div className="mt-4">
            <DateFilter
              onFilterChange={(filterData) => {
                if (filterData.filterType === 'exact') {
                  setStartDate(filterData.exactDate);
                  setEndDate(filterData.exactDate);
                } else {
                  setStartDate(filterData.startDate);
                  setEndDate(filterData.endDate);
                }
              }}
            />
          </div>

          {/* Category Filter Row */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold">Filter by Revenue Categories (Optional)</Label>
              {selectedCategories.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCategories([])}
                  className="text-xs"
                >
                  Clear ({selectedCategories.length})
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {getUniqueCategories().map((category) => (
                <label
                  key={category.name}
                  className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCategories([...selectedCategories, category.name]);
                      } else {
                        setSelectedCategories(selectedCategories.filter(name => name !== category.name));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{category.name}</span>
                </label>
              ))}
            </div>
            {selectedCategories.length === 0 && (
              <p className="text-xs text-gray-500 mt-2">Select one or more categories to filter revenue data. Leave empty to include all categories.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Table */}
      {reportData.length > 0 && (
        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Report Results
                </CardTitle>
                <CardDescription>
                  {reportData.length} {reportData.length === 1 ? 'entry' : 'entries'} found
                </CardDescription>
              </div>
              <Button onClick={downloadCSV} variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                Download CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    {groupBy === "brand" && (
                      <>
                        <th className="text-left p-3 font-semibold">Brand</th>
                        <th className="text-right p-3 font-semibold">Total Revenue</th>
                        <th className="text-right p-3 font-semibold">Entries</th>
                        <th className="text-left p-3 font-semibold">Category Breakdown</th>
                      </>
                    )}
                    {groupBy === "restaurant" && (
                      <>
                        <th className="text-left p-3 font-semibold">Restaurant</th>
                        <th className="text-right p-3 font-semibold">Total Revenue</th>
                        <th className="text-right p-3 font-semibold">Entries</th>
                        <th className="text-left p-3 font-semibold">Category Breakdown</th>
                      </>
                    )}
                    {groupBy === "category" && (
                      <>
                        <th className="text-left p-3 font-semibold">Category</th>
                        <th className="text-right p-3 font-semibold">Total Revenue</th>
                        <th className="text-right p-3 font-semibold">Entries</th>
                      </>
                    )}
                    {groupBy === "date" && (
                      <>
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-right p-3 font-semibold">Total Revenue</th>
                        <th className="text-right p-3 font-semibold">Entries</th>
                      </>
                    )}
                    {groupBy === "all" && (
                      <>
                        <th className="text-left p-3 font-semibold">Restaurant</th>
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-right p-3 font-semibold">Total Revenue</th>
                        <th className="text-right p-3 font-semibold">Entries</th>
                        <th className="text-left p-3 font-semibold">Categories</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      {groupBy === "brand" && (
                        <>
                          <td className="p-3">{row.brand_name}</td>
                          <td className="p-3 text-right font-semibold">
                            <CurrencyDisplay amount={row.total_revenue} />
                          </td>
                          <td className="p-3 text-right">{row.entries}</td>
                          <td className="p-3">
                            <div className="space-y-1">
                              {Object.entries(row.categories || {}).map(([catId, amount], idx) => {
                                const category = revenueCategories.find(c => c.id === catId);
                                const catName = category ? category.name : "Unknown";
                                return (
                                  <div key={idx} className="text-sm">
                                    <span className="font-medium">{catName}:</span>{" "}
                                    <CurrencyDisplay amount={amount} />
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </>
                      )}
                      {groupBy === "restaurant" && (
                        <>
                          <td className="p-3">{row.restaurant_name}</td>
                          <td className="p-3 text-right font-semibold">
                            <CurrencyDisplay amount={row.total_revenue} />
                          </td>
                          <td className="p-3 text-right">{row.entries}</td>
                          <td className="p-3">
                            <div className="space-y-1">
                              {Object.entries(row.categories || {}).map(([catId, amount], idx) => {
                                const category = revenueCategories.find(c => c.id === catId);
                                const catName = category ? category.name : "Unknown";
                                return (
                                  <div key={idx} className="text-sm">
                                    <span className="font-medium">{catName}:</span>{" "}
                                    <CurrencyDisplay amount={amount} />
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </>
                      )}
                      {groupBy === "category" && (
                        <>
                          <td className="p-3">{row.category_name}</td>
                          <td className="p-3 text-right font-semibold">
                            <CurrencyDisplay amount={row.total_revenue} />
                          </td>
                          <td className="p-3 text-right">{row.entries}</td>
                        </>
                      )}
                      {groupBy === "date" && (
                        <>
                          <td className="p-3">{formatDateDDMonYYYY(row.date)}</td>
                          <td className="p-3 text-right font-semibold">
                            <CurrencyDisplay amount={row.total_revenue} />
                          </td>
                          <td className="p-3 text-right">{row.entries}</td>
                        </>
                      )}
                      {groupBy === "all" && (
                        <>
                          <td className="p-3">{row.restaurant_name}</td>
                          <td className="p-3">{formatDateDDMonYYYY(row.date)}</td>
                          <td className="p-3 text-right font-semibold">
                            <CurrencyDisplay amount={row.total_revenue} />
                          </td>
                          <td className="p-3 text-right">{row.entries}</td>
                          <td className="p-3">
                            <div className="space-y-1">
                              {Object.values(row.categories).map((cat, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="font-medium">{cat.name}:</span>{" "}
                                  <CurrencyDisplay amount={cat.amount} />
                                </div>
                              ))}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && reportData.length === 0 && (
        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl">
          <CardContent className="flex items-center justify-center p-12">
            <div className="text-center text-gray-500">
              <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No Report Generated</h3>
              <p className="text-sm">Configure filters and click "Generate Report" to see results</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsTable;
